import { setChatServicesData } from "../Store/Slices/ChatServicesSlice";
import {
  addNewMessageWithValidation,
  streamChunk,
  streamEnd,
  streamStart,
} from "../Store/Slices/ChatSocketSlice";
import {
  getUserIdFromUser,
  getUserImage,
  normalizeWebSocketMessage,
} from "../Pages/ChatHuman/utils/normalizeMessage";

// Where a message coming in off the chat socket is put into the store.
//
// This is deliberately not a screen's job. A question asked from the card is
// answered while the panel is shut and no screen is on show, and a listener
// that lives inside a screen is torn down the moment that screen unmounts — so
// the question went out, the listener went with it, and the answer arrived
// half a minute later to nobody. Listening has to outlive the screens.
//
// A screen that IS on show still has its own work to do with the same message:
// scrolling, the handoff banner, reading a reply aloud. It hears about it from
// here rather than from the socket, so however many screens are watching, a
// message is still put into the store exactly once.

export const HANDOFF_MARKER = "[TRANSFER_TO_HUMAN]";
export const CONNECTING_MESSAGE = "Connecting you to an agent...";

// What the frame turned out to be, so a screen never has to read it again.
export const INCOMING_STREAM_START = "stream_start";
export const INCOMING_STREAM_CHUNK = "stream_chunk";
export const INCOMING_STREAM_END = "stream_end";
export const INCOMING_MESSAGE = "message";

const STREAM_START_OPERATION = "STREAM_START";
const STREAM_CHUNK_OPERATION = "STREAM_CHUNK";
const STREAM_END_OPERATION = "STREAM_END";

const listeners = [];

/**
 * A screen asks to be told about a message that has already been stored.
 * Returns the function that stops it being told.
 */
export const onIncomingChatMessage = (listener) => {
  listeners.push(listener);
  return () => {
    const index = listeners.indexOf(listener);
    if (index !== -1) listeners.splice(index, 1);
  };
};

const announce = (event) => {
  // A copy, so a screen that unsubscribes as it reacts cannot skip the next one.
  listeners.slice().forEach((listener) => {
    try {
      listener(event);
    } catch (e) {
      // One screen failing to react must not stop the rest, and must never
      // stop the message reaching the store.
    }
  });
  return event;
};

/** The socket hands over an object, a string, or an envelope around either. */
export const parseIncomingChatMessage = (payload) => {
  let message = payload;
  if (payload?.message) message = payload.message;
  if (typeof message === "string") {
    try {
      message = JSON.parse(message);
    } catch (e) {
      return null;
    }
  }
  return message && typeof message === "object" ? message : null;
};

/**
 * Put one incoming frame into the store, then tell any screen about it.
 *
 * @param payload  what the socket delivered
 * @param context  dispatch, who the customer is, and which conversation is
 *                 already theirs; chatSettings only to switch to human support
 * @returns        what the frame was, or null if it was not a message at all
 */
export const handleIncomingChatMessage = (payload, context) => {
  const message = parseIncomingChatMessage(payload);
  if (!message) return null;

  const {
    dispatch,
    user = null,
    activeChatId = null,
    fallbackChatId = null,
    chatSettings = null,
  } = context || {};

  const operation = String(message.messageOperationType || "").toUpperCase();

  // A reply being written. Nothing is stored as a message until it ends.
  if (operation === STREAM_START_OPERATION) {
    dispatch(streamStart({ chatId: message.chatId, senderName: message.senderName }));
    return announce({ type: INCOMING_STREAM_START, chatId: message.chatId || null });
  }
  if (operation === STREAM_CHUNK_OPERATION) {
    dispatch(streamChunk({ content: message.content }));
    return announce({ type: INCOMING_STREAM_CHUNK, chatId: message.chatId || null });
  }

  const currentUserId = getUserIdFromUser(user);
  const normalized = normalizeWebSocketMessage(message, {
    currentUserId,
    currentUserImage: getUserImage(user),
  });

  if (operation === STREAM_END_OPERATION) {
    dispatch(streamEnd(normalized));
    return announce({
      type: INCOMING_STREAM_END,
      message: normalized,
      chatId: normalized.chatId || null,
    });
  }

  // The backend asks for a human by marking the text rather than by sending
  // anything of its own, so the marker is taken out before the customer sees it.
  const content = typeof normalized.content === "string" ? normalized.content : "";
  const isHandoffNotice = content.includes(HANDOFF_MARKER);
  if (isHandoffNotice) {
    normalized.content = content.replace(HANDOFF_MARKER, "").trim() || CONNECTING_MESSAGE;
    normalized.isHandoffNotice = true;
    if (chatSettings && chatSettings.support !== "human") {
      dispatch(setChatServicesData({ chatSettingsData: { ...chatSettings, support: "human" } }));
    }
  }

  const chatId = normalized.chatId || normalized.chat?.id || null;
  dispatch(
    addNewMessageWithValidation({
      message: normalized,
      currentChatId: activeChatId || chatId || fallbackChatId,
      currentUserId,
    }),
  );

  return announce({ type: INCOMING_MESSAGE, message: normalized, chatId, isHandoffNotice });
};
